import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { clsx as cx } from 'clsx';
import Icon from '../icon';
import NotificationsMenu from '../notifications-menu';
import AvatarMenu from '../avatar-menu';
import Link from '../link';
import ThemeSwitcher from '../theme-switcher';

import { Container, Menu, MenuLabel } from './main-menu.css';
import UserService from '../../../services/user-service';

import {
	useUser,
	UserButton
} from '@clerk/clerk-react';

// Tailwind classes for a nav link row (shared between page links and queue links)
const navLinkCx = (isActive) => cx(
	'nav-row',
	'flex items-center flex-nowrap',
	'border-0 border-l-2 border-solid bg-transparent p-0',
	'text-sm font-bold focus:outline-none',
	'hover:bg-nav-hover hover:text-white transition-colors duration-fast',
	isActive
		? 'nav-row--active border-primary text-white'
		: 'border-transparent text-white text-opacity-30'
);

function MainMenu(props) {
	const {
		links,
		queues,
		session,
		onLogout,
		notifications,
		onNotificationsClick,
		onMenuItemClick,
		onNavigate,
		isNotificationsActive,
		isMaximized
	} = props;

	const isGuest = UserService.isGuest(session);

	const { user: isClerk } = useUser();

	const [activeLink, setActiveLink] = useState(
		links.find((link) => link.active) || queues.find((queue) => queue.active)
	);

	useEffect(() => {
		setActiveLink(links.find((link) => link.active) || queues.find((queue) => queue.active));
	}, [links, queues]);

	const handleNavigate = (item) => {
		setActiveLink(item);
		onMenuItemClick && onMenuItemClick(item);
		onNavigate && onNavigate(item);
	};

	const renderPageLinks = () => {
		return links.map((link) => {
			const isActive = activeLink?.label === link.label;

			return link.enabled && (
				<li
					key={link.sref}
					className="relative outline-none"
					data-tooltip={link.label}
					data-tooltip-position="right"
				>
					{link.onClick
						? (
							<button
								type="button"
								className={navLinkCx(isActive)}
								data-cy={`menu-${link.sref}`}
								data-testid={`menu-${link.sref}`}
								onClick={() => {
									handleNavigate(link);
									link.onClick();
								}}
							>
								<Icon use={link.icon} size={1.25} />
								{ !!link.badge && <span className="badge">{link.badge}</span> }
								<MenuLabel className={isMaximized ? 'block' : 'hidden'}>{link.label}</MenuLabel>
							</button>
						)
						: (
							<Link
								to={{ state: link.sref }}
								className={navLinkCx(isActive)}
								data-cy={`menu-${link.sref}`}
								data-testid={`menu-${link.sref}`}
								onClick={() => handleNavigate(link)}
							>
								<Icon use={link.icon} size={1.25} />
								{ !!link.badge && <span className="badge">{link.badge}</span> }
								<MenuLabel className={isMaximized ? 'block' : 'hidden'}>{link.label}</MenuLabel>
							</Link>
						)}
				</li>
			);
		});
	};

	const renderQueueLinks = () => {
		return queues.map((queue, i) => {
			const isActive = queue.label === activeLink?.label;

			return queue.enabled && (
				<li
					key={queue.sref}
					className={cx('relative outline-none', { 'mt-8': i === 0 })}
					data-tooltip={queue.label}
					data-tooltip-position="right"
				>
					<Link
						to={{ state: queue.sref }}
						className={navLinkCx(isActive)}
						data-cy={`menu-${queue.sref}`}
						data-testid={`menu-${queue.sref}`}
						onClick={() => handleNavigate(queue)}
					>
						<Icon use={queue.icon} size={1.25} />
						{ !!queue.badge && <span className="badge">{queue.badge}</span> }
						<MenuLabel className={isMaximized ? 'block' : 'hidden'}>{queue.label}</MenuLabel>
					</Link>
				</li>
			);
		});
	};

	return (
		<Container isMaximized={isMaximized} data-cy="main-menu">
			<Menu>
				{renderPageLinks()}
				{renderQueueLinks()}
			</Menu>

			<div>
				<ThemeSwitcher isMaximized={isMaximized} onMenuItemClick={onMenuItemClick} />
				{ !isGuest && (
					<NotificationsMenu
						hasUnread={notifications.badge}
						isActive={isNotificationsActive}
						onMenuItemClick={onNotificationsClick}
						isMaximized={isMaximized}
					/>
				)}
				{isClerk
					? (
						<div className="flex justify-center items-center">
							<UserButton />
						</div>
					)
					: (
						<AvatarMenu
							onMenuItemClick={onMenuItemClick}
							onLogout={onLogout}
							session={session}
							isMaximized={isMaximized}
						/>
					)}
			</div>
		</Container>
	);
}

MainMenu.propTypes = {
	links: PropTypes.array.isRequired,
	session: PropTypes.object.isRequired,
	queues: PropTypes.array.isRequired,
	onLogout: PropTypes.func.isRequired,
	notifications: PropTypes.object.isRequired,
	onNotificationsClick: PropTypes.func.isRequired,
	onMenuItemClick: PropTypes.func,
	onNavigate: PropTypes.func,
	isNotificationsActive: PropTypes.bool.isRequired,
	isMaximized: PropTypes.bool.isRequired
};

MainMenu.methodProps = [
	'onLogout',
	'onNotificationsClick'
];

export default MainMenu;
